import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LookupSetup } from '@models/lookup-setup';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { TrackingService } from '@services/tracking/tracking.service';
import { backoff } from '@shared/helpers/backoff';
import { of } from 'rxjs';
import { catchError, defaultIfEmpty, map, tap } from 'rxjs/operators';

const defaultEmptyObject: LookupSetup = {
  Name: null,
  Mnemonic: null,
  ResultType: null,
  DataTableType: null,
  Dimensions: 0,
};

@Injectable({ providedIn: 'root' })
export class TableSetupService {
  private environment: AppConfigAPI;
  constructor(private http: HttpClient, private trackingService: TrackingService, private appConfig: AppConfigProvider) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  getLookupTableGlobalParams() {
    const url = this.environment.LOOKUPAPI + '/api/lookup/getparams';
    return this.http.get(url).pipe(backoff(3, 250));
  }

  /**
   * Get Lookup Table Definition
   */
  getFilteredLookupTables(tableTypesToDiscard: string[] = []) {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/getall`;
    return this.http.get(url).pipe(
      defaultIfEmpty([{ ...defaultEmptyObject }]),
      backoff(3, 250),
      map((collection: LookupSetup[]) => {
        if (collection) {
          const lookupSetupNoData = collection
            .filter((item: LookupSetup) => tableTypesToDiscard && !tableTypesToDiscard.includes(item.DataTableType))
            .map(this.flattenApiResponse());
          return lookupSetupNoData && lookupSetupNoData.length > 0 ? lookupSetupNoData : [{ ...defaultEmptyObject }];
        }
        return collection || [{ ...defaultEmptyObject }];
      })
    );
  }

  /**
   * Obtain the whole Lookup Tables data set, including dimension definition, results and metadata
   */
  getLookupTablesWithData() {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/getallbyuser`;
    return this.http.get(url).pipe(
      backoff(3, 250),
      tap((results: any[]) => {
        this.trackingService.updateModelDataChangesFlags(
          'lookupTables',
          results.some(res => res.Update === true),
          'Fetching Lookup data and tested if have tracking info'
        );
      })
    );
  }

  /**
   * Get lookup setup definition for an lookup table id that would be passed as parameter, otherwise returns all
   */
  getLookupTableSetup(id?: number): any {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/getsetup${id ? `/${id}` : ''}`;
    return this.http.get(url).pipe(backoff(3, 250));
  }

  addLookupTableSetup(data) {
    console.log('TCL ~ file: table-setup.service.ts:79 ~ TableSetupService ~ addLookupTableSetup ~ data:', data);
    const url = `${this.environment.LOOKUPAPI}/api/lookup/addtable`;
    const payload = data.map(flatObject => ({
      ...this.unflattenDimensionArray(flatObject),
      Mnemonic: flatObject.Mnemonic?.toString(),
      Name: flatObject.Name?.toString(),
    }));
    return this.http.post(url, payload);
  }

  deleteLookupTableSetup(data) {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/deletetable`;
    const rowsToDelete = data.map(item => ({
      Id: item.originalItem.Id,
    }));

    const body = { json: JSON.stringify(rowsToDelete), user_id: -1 };

    return this.http.post(url, body);
  }

  updateLookupTableSetup(data) {
    const url = `${this.environment.LOOKUPAPI}/api/lookup/updatetable`;
    const user_id = -1;
    const updating = [];
    if (data) {
      data.map(dataItem => {
        const mnemonic = dataItem.originalItem.Mnemonic;
        let flat: any = {};
        if (dataItem.item) {
          flat = this.unflattenDimensionArray(dataItem.item);
        } else {
          flat.Id = dataItem.originalItem.Id;
        }
        updating.push({
          ...flat,
          OldMnemonic: mnemonic,
          Mnemonic: dataItem.item.Mnemonic?.toString(),
          Name: dataItem.item.Name?.toString(),
        });
      });
    }
    const body = { json: JSON.stringify(updating), user_id };
    return this.http.post(url, body).pipe(
      catchError(err => {
        console.warn(`updateLookupTableSetup`, err);
        return of([]);
      })
    );
  }

  private flattenApiResponse(): any {
    return item => {
      let flattenedDimensionSetup = {};
      if (item.DimensionList) {
        item.DimensionList.map(this.flattenDimensionArray()).map(toAppend => {
          flattenedDimensionSetup = {
            ...flattenedDimensionSetup,
            ...toAppend,
          };
        });
      }
      const result = {
        ...item,
        Dimensions: 0 || (item.DimensionList && item.DimensionList.length),
        ...flattenedDimensionSetup,
      };
      delete result.Data;
      delete result.DimensionList;
      return result;
    };
  }

  private flattenDimensionArray(): any {
    return dim => {
      const num = dim.DimensionNumber;
      return {
        [`DataType${num}`]: dim.DataType ? dim.DataType : '',
        [`DimExtension${num}`]: dim.DimExtension ? dim.DimExtension : '',
        [`DimMnemonic${num}`]: dim.DimMnemonic ? dim.DimMnemonic : '',
        [`Interpolation${num}`]: dim.Interpolation ? dim.Interpolation : '',
      };
    };
  }

  private unflattenDimensionArray(flatObj: any) {
    flatObj.DimensionList = [];
    for (let num = 1; num <= flatObj.Dimensions; num++) {
      const dimensionListElement = {
        DataType: flatObj[`DataType${num}`],
        DimMnemonic: flatObj[`DimMnemonic${num}`],
        Interpolation: flatObj[`Interpolation${num}`],
        DimensionNumber: num,
        TableMnemonic: flatObj.Mnemonic?.toString(),
      };
      flatObj.DimensionList.push(dimensionListElement);
    }
    return flatObj;
  }
}
